"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useRouter } from "next/navigation";
import { HugeiconsIcon } from "@hugeicons/react";
import { SearchIcon } from "@hugeicons/core-free-icons";
import type { DateRange } from "react-day-picker";
import * as React from "react";
import DateRangePicker from "../ui/date-range-picker";
import { DestinationSearch } from "./DestinationSearch";
import { Typography } from "../ui/typography";
import { PeopleCountInput } from "./PeopleCountInput";
import { Button } from "../ui/button";
import { SessionManager } from "@/lib/session/SessionManager";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const searchSchema = z.object({
  destination: z.string().min(1, "مقصد را وارد کنید"),
  dateRange: z.object({
    from: z.date({ required_error: "تاریخ شروع را وارد کنید" }),
    to: z.date({ required_error: "تاریخ پایان را وارد کنید" }),
  }),
  peopleCount: z.number().optional(),
});

type SearchFormData = z.infer<typeof searchSchema>;

export default function QuickSearch({ className }: { className?: string }) {
  const router = useRouter();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>();

  // State for managing autofocus chain
  const [dateRangeOpen, setDateRangeOpen] = React.useState(false);
  const [peopleCountOpen, setPeopleCountOpen] = React.useState(false);
  const peopleCountOpeningRef = React.useRef(false);

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SearchFormData>({
    defaultValues: {
      dateRange: {
        from: new Date(),
        to: new Date(new Date().setDate(new Date().getDate() + 1)),
      },
    },
    mode: "onChange",
    resolver: zodResolver(searchSchema),
  });

  // Handle form submission and redirect with query params
  const onSubmit = (data: SearchFormData) => {
    // Parse destination format: "city--state" or just "city"
    const destination = data.destination;
    const [city, state] = destination.includes("--")
      ? destination.split("--")
      : [destination, ""];

    // Create new search session (new funnel)
    const sessionId = SessionManager.startSearchSession(
      city,
      state || "unknown",
    );

    const params = new URLSearchParams();
    params.set("checkIn", data.dateRange.from.toISOString().split("T")[0]);
    params.set("checkOut", data.dateRange.to.toISOString().split("T")[0]);
    if (data.peopleCount) {
      params.set("peopleCount", data.peopleCount.toString());
    }
    router.push(`/search/city/${data.destination}?${params.toString()}`);
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
    if (range?.from) {
      setValue("dateRange.from", range.from);
    }
    if (range?.to) {
      setValue("dateRange.to", range.to);
    }
  };

  return (
    <div
      className={`bg-white md:h-[72px] border border-gray-100 rounded-3xl p-4 md:pl-3 md:pr-6 md:py-3.5 flex items-center gap-4 w-full max-w-2xl mx-auto ${className}`}
    >
      {/* Form container */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 w-full">
        <DestinationSearch
          value={watch("destination")}
          onChange={(value) => setValue("destination", value)}
          error={errors.destination?.message}
          onSelectionComplete={() => {
            // When destination is selected, open date range picker
            setTimeout(() => setDateRangeOpen(true), 100);
          }}
        />
        <div className="w-px h-6 hidden md:block bg-gray-100"></div>
        <div className="relative flex-1 border border-gray-100 md:border-none rounded-lg md:p-0.5">
          <DateRangePicker
            dateRange={dateRange}
            onSelect={handleDateRangeChange}
            className="w-full"
            open={dateRangeOpen}
            onOpenChange={setDateRangeOpen}
            onSelectionComplete={() => {
              // On mobile, go to PLP as soon as dates are confirmed —
              // no extra tap on the search button.
              if (!isDesktop) {
                handleSubmit(onSubmit)();
                return;
              }

              // When date range is confirmed, open people count input
              // Use a longer delay to ensure date picker is fully closed
              setTimeout(() => {
                peopleCountOpeningRef.current = true;
                setPeopleCountOpen(true);
                // Reset the flag after a short delay
                setTimeout(() => {
                  peopleCountOpeningRef.current = false;
                }, 200);
              }, 200);
            }}
          />
          {(errors.dateRange?.from || errors.dateRange?.to) && (
            <p className="text-red-500 text-sm mt-1">
              {errors.dateRange?.from?.message || errors.dateRange?.to?.message}
            </p>
          )}
        </div>
        <div className="w-px h-6 hidden md:block bg-gray-100"></div>
        <PeopleCountInput
          value={watch("peopleCount")}
          onChange={(value) => setValue("peopleCount", value)}
          error={errors.peopleCount?.message}
          open={peopleCountOpen}
          onOpenChange={(open) => {
            // Prevent closing if we just programmatically opened it
            if (!open && peopleCountOpeningRef.current) {
              return;
            }
            setPeopleCountOpen(open);
          }}
        />

        {/* Search Button */}
        <Button
          type="button"
          variant="secondary"
          onClick={handleSubmit(onSubmit)}
          className="w-full md:w-11 h-11 md:bg-primary md:text-primary-foreground md:hover:bg-primary/90"
        >
          <Typography className="text-[13px] font-medium md:hidden">
            جستجو
          </Typography>
          <HugeiconsIcon
            icon={SearchIcon}
            size={20}
            className="hidden md:block size-5"
            strokeWidth={1.5}
          />
        </Button>
      </div>
    </div>
  );
}
